import { useAsyncAction } from "../hooks/useAsyncAction";
import { useEffect, useLayoutEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import { Input } from "@components/ui/input";
import { Textarea } from "@components/ui/textarea";
import { Label } from "@components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@components/ui/empty";
import { Skeleton } from "@components/ui/skeleton";
import {
  applyWorkflowTemplate,
  exportEntityAuditLog,
  getEntityDetail,
  getWorkflow,
  overrideAssignment,
  updateWorkflowStep,
  type EntityDetail,
  type WorkflowStep,
  type WorkflowTemplateSummary,
} from "../lib/auth";
import { overrideAssignmentSchema } from "@io-spoke/contracts/assignments";
import {
  overrideOwnerRequiredMessage,
  overrideRationaleRequiredMessage,
} from "../lib/overrideValidationMessages";
import { PageHeader } from "@components/layout/PageHeader";
import { PageShell } from "@components/layout/PageShell";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@components/ui/breadcrumb";
import { SidebarTrigger } from "@components/ui/sidebar";
import { EntityIntegrationExportCard } from "@components/integrations/EntityIntegrationExportCard";
import { useOrgContext } from "@components/layout/OrgContext";

type EntityDetailPageProps = {
  entityId: string;
  navigate: (route: string) => void;
};

const overrideFormSchema = overrideAssignmentSchema.omit({ isFallback: true }).extend({
  assigneeId: z.string().trim().min(1, overrideOwnerRequiredMessage),
  rationale: z.string().trim().min(1, overrideRationaleRequiredMessage),
});
type OverrideFormValues = z.infer<typeof overrideFormSchema>;

const auditExportFormSchema = z
  .object({
    from: z.string(),
    to: z.string(),
    format: z.enum(["csv", "markdown"]),
  })
  .superRefine((values, ctx) => {
    if (values.from && values.to && new Date(values.from) > new Date(values.to)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["to"], message: "From date cannot be after To date." });
    }
  });
type AuditExportFormValues = z.infer<typeof auditExportFormSchema>;
const auditExportFormDefaults: AuditExportFormValues = { from: "", to: "", format: "csv" };

export function EntityDetailPage({
  entityId,
  navigate,
}: EntityDetailPageProps) {
  const { currentMembership } = useOrgContext();
  const templateAction = useAsyncAction();
  const overrideAction = useAsyncAction();
  const auditExportAction = useAsyncAction();
  const [entityDetail, setEntityDetail] = useState<EntityDetail | null>(null);
  const [isEntityDetailLoading, setIsEntityDetailLoading] = useState(false);
  const [entityDetailError, setEntityDetailError] = useState<string | null>(null);
  const [workflowSteps, setWorkflowSteps] = useState<WorkflowStep[]>([]);
  const [workflowTemplate, setWorkflowTemplate] = useState<string | null>(null);
  const [workflowTemplates, setWorkflowTemplates] = useState<
    WorkflowTemplateSummary[]
  >([]);
  const [selectedWorkflowTemplate, setSelectedWorkflowTemplate] = useState("");
  const [workflowError, setWorkflowError] = useState<string | null>(null);
  const noTemplateValue = "__no_template__";
  const [overrideSubmitError, setOverrideSubmitError] = useState<string | null>(null);
  const [auditExportError, setAuditExportError] = useState<string | null>(null);
  const [visibleTimelineCount, setVisibleTimelineCount] = useState(50);

  const overrideForm = useForm<OverrideFormValues>({
    resolver: zodResolver(overrideFormSchema),
    defaultValues: {
      assigneeType: "team",
      assigneeId: "",
      rationale: "",
    },
  });

  const auditExportForm = useForm<AuditExportFormValues>({
    resolver: zodResolver(auditExportFormSchema),
    defaultValues: auditExportFormDefaults,
  });

  useEffect(() => {
    if (!entityId) {
      return;
    }

    let isActive = true;
    setIsEntityDetailLoading(true);
    setEntityDetailError(null);
    setWorkflowError(null);
    Promise.all([getEntityDetail(entityId), getWorkflow(entityId)])
      .then(([detail, workflow]) => {
        if (!isActive) {
          return;
        }
        setEntityDetail(detail);
        setWorkflowSteps(workflow.steps);
        setWorkflowTemplate(workflow.template);
        setWorkflowTemplates(workflow.templates);
        setSelectedWorkflowTemplate(workflow.template);
      })
      .catch((err) => {
        if (!isActive) {
          return;
        }
        const message =
          err instanceof Error ? err.message : "Unable to load this work item.";
        setEntityDetailError(message);
        setWorkflowError(message);
      })
      .finally(() => {
        if (isActive) {
          setIsEntityDetailLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [entityId]);

  const breadcrumb = (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <button type="button" onClick={() => navigate("/review")}>
              Review queue
            </button>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>Work item</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );

  // Populate the fetched assignment before the visible editor can accept input.
  useLayoutEffect(() => {
    overrideForm.reset({
      assigneeType: entityDetail?.assignment?.assigneeType ?? "team",
      assigneeId: entityDetail?.assignment?.assigneeId ?? "",
      rationale: "",
    });
    setOverrideSubmitError(null);
  }, [entityId, entityDetail?.assignment?.assigneeId, entityDetail?.assignment?.assigneeType, overrideForm.reset]);

  useEffect(() => {
    auditExportForm.reset(auditExportFormDefaults);
    setAuditExportError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityDetail?.entity?.id]);

  const selectedWorkflowSummary = workflowTemplates.find(
    (template) => template.name === selectedWorkflowTemplate
  );

  const handleTemplateApply = async () => templateAction.run(async () => {
    if (!entityDetail?.entity) {
      setWorkflowError("Select a work item before updating the workflow.");
      return;
    }
    if (!selectedWorkflowTemplate) {
      setWorkflowError("Select a workflow template.");
      return;
    }
    if (selectedWorkflowTemplate === workflowTemplate) {
      setWorkflowError("That template is already active.");
      return;
    }

    try {
      setWorkflowError(null);
      const updated = await applyWorkflowTemplate(
        entityDetail.entity.id,
        selectedWorkflowTemplate
      );
      setWorkflowSteps(updated.steps);
      setWorkflowTemplate(updated.template);
      setWorkflowTemplates(updated.templates);
      const refreshed = await getEntityDetail(entityDetail.entity.id);
      setEntityDetail(refreshed);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to update workflow.";
      setWorkflowError(message);
    }
  });

  const handleAuditExport = auditExportForm.handleSubmit((values) => auditExportAction.run(async () => {
    if (!entityDetail?.entity) {
      return;
    }
    setAuditExportError(null);
    try {
      const blob = await exportEntityAuditLog(entityDetail.entity.id, {
        from: values.from ? new Date(values.from).toISOString() : undefined,
        to: values.to ? new Date(values.to).toISOString() : undefined,
        format: values.format,
      });
      const extension = values.format === "csv" ? "csv" : "md";
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `entity-${entityDetail.entity.id}-audit.${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Unable to export the audit log.";
      setAuditExportError(message);
    }
  }));

  const handleOverride = overrideForm.handleSubmit((values) => overrideAction.run(async () => {
    if (!entityDetail?.entity) {
      return;
    }
    setOverrideSubmitError(null);
    try {
      await overrideAssignment(entityDetail.entity.id, {
        assigneeType: values.assigneeType,
        assigneeId: values.assigneeId,
        rationale: values.rationale,
      });
      const refreshed = await getEntityDetail(entityDetail.entity.id);
      setEntityDetail(refreshed);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unable to override assignment.";
      setOverrideSubmitError(message);
    }
  }));

  return (
    <PageShell maxWidth="6xl">
      <PageHeader
        breadcrumb={breadcrumb}
        leading={<SidebarTrigger />}
        eyebrow="Work Item"
        title={entityDetail?.entity?.title ?? "Work item detail"}
        actions={
          <>
            <Button variant="outline" onClick={() => navigate("/review")}>
              Back to review
            </Button>
          </>
        }
      />

      <section className="mt-8 space-y-6">
        {isEntityDetailLoading ? (
          <div className="space-y-4">
            <Card className="border-border bg-card/60">
              <CardHeader className="space-y-2">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-3 w-36" />
              </CardHeader>
              <CardContent className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
              </CardContent>
            </Card>
            <Card className="border-border bg-card/60">
              <CardHeader className="space-y-2">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-3 w-24" />
              </CardHeader>
              <CardContent className="space-y-2">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={`workflow-skeleton-${index}`} className="h-4 w-full" />
                ))}
              </CardContent>
            </Card>
          </div>
        ) : entityDetailError ? (
          <p className="text-sm text-red-400" role="alert">
            {entityDetailError}
          </p>
        ) : entityDetail ? (
          <>
            <EntityIntegrationExportCard
              entityId={entityDetail.entity.id}
              entityStatus={entityDetail.entity.status}
              canManageIntegrations={["owner", "admin"].includes(currentMembership?.role ?? "")}
              navigate={navigate}
            />
            <Card className="border-border bg-card/60">
              <CardHeader>
                <CardTitle className="text-lg">Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-foreground/90">
                  <div>
                    <span className="text-muted-foreground/70">Type:</span>{" "}
                    {entityDetail.entity.type}
                  </div>
                  <div>
                    <span className="text-muted-foreground/70">Priority:</span>{" "}
                    {entityDetail.entity.priority}
                  </div>
                  <div>
                    <span className="text-muted-foreground/70">Status:</span>{" "}
                    {entityDetail.entity.status}
                  </div>
                  <div>
                    <span className="text-muted-foreground/70">Description:</span>{" "}
                    {entityDetail.entity.description}
                  </div>
                  <div>
                    <span className="text-muted-foreground/70">Impacted systems:</span>{" "}
                    {entityDetail.entity.systems.length > 0
                      ? entityDetail.entity.systems.join(", ")
                      : "None"}
                  </div>
                  <div>
                    <span className="text-muted-foreground/70">Customer impact:</span>{" "}
                    {entityDetail.entity.customerImpact ?? "Not specified"}
                  </div>
                  <div>
                    <span className="text-muted-foreground/70">Customer:</span>{" "}
                    {entityDetail.entity.customer ? (
                      <button
                        type="button"
                        className="text-left text-foreground/90 underline decoration-dotted underline-offset-4 hover:text-white"
                        onClick={() =>
                          navigate(`/customers/${entityDetail.entity.customer?.id}`)
                        }
                      >
                        {entityDetail.entity.customer.name ??
                          entityDetail.entity.customer.email ??
                          "Unknown customer"}
                      </button>
                    ) : (
                      "Unassigned"
                    )}
                  </div>
                  <div>
                    <span className="text-muted-foreground/70">Suggested owner:</span>{" "}
                    {entityDetail.entity.suggestedOwner
                      ? `${entityDetail.entity.suggestedOwner.type} ${entityDetail.entity.suggestedOwner.id}`
                      : "None"}
                  </div>
                  <div>
                    <span className="text-muted-foreground/70">Owner:</span>{" "}
                    {entityDetail.assignment
                      ? `${entityDetail.assignment.assigneeType} ${entityDetail.assignment.assigneeId}`
                      : "Unassigned"}
                  </div>
                  {entityDetail.entity.slaTargetAt ? (
                    <div>
                      <span className="text-muted-foreground/70">SLA target:</span>{" "}
                      {new Date(entityDetail.entity.slaTargetAt).toLocaleString()}
                      {entityDetail.entity.slaBreachedAt ? (
                        <span className="ml-2 text-red-300">
                          Breached{" "}
                          {new Date(
                            entityDetail.entity.slaBreachedAt
                          ).toLocaleString()}
                        </span>
                      ) : null}
                    </div>
                  ) : null}
                  {entityDetail.assignment?.rationale ? (
                    <div>
                      <span className="text-muted-foreground/70">Routing rationale:</span>{" "}
                      {entityDetail.assignment.rationale}
                    </div>
                  ) : null}
              </CardContent>
            </Card>
            <Card className="border-border bg-card/60">
              <CardHeader>
                <CardTitle className="text-lg">Override assignment</CardTitle>
                <CardDescription>
                  Adjust owner/team and capture a rationale for the change.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <form className="space-y-3" onSubmit={handleOverride} noValidate>
                  <div className="space-y-2">
                    <Label htmlFor="override-type">Owner type</Label>
                    <Controller
                      name="assigneeType"
                      control={overrideForm.control}
                      render={({ field }) => (
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger id="override-type">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="team">team</SelectItem>
                            <SelectItem value="role">role</SelectItem>
                            <SelectItem value="agent">agent</SelectItem>
                            <SelectItem value="human">human</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="override-id">Owner</Label>
                    <Input
                      id="override-id"
                      type="text"
                      placeholder="engineering"
                      {...overrideForm.register("assigneeId")}
                      aria-invalid={Boolean(overrideForm.formState.errors.assigneeId)}
                      aria-describedby={overrideForm.formState.errors.assigneeId ? "override-id-error" : undefined}
                    />
                    {overrideForm.formState.errors.assigneeId ? (
                      <p id="override-id-error" className="text-xs text-red-400" role="alert">
                        {overrideForm.formState.errors.assigneeId.message}
                      </p>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="override-rationale">Rationale</Label>
                    <Textarea
                      id="override-rationale"
                      placeholder="Routing to platform team due to workload."
                      {...overrideForm.register("rationale")}
                      aria-invalid={Boolean(overrideForm.formState.errors.rationale)}
                      aria-describedby={overrideForm.formState.errors.rationale ? "override-rationale-error" : undefined}
                    />
                    {overrideForm.formState.errors.rationale ? (
                      <p id="override-rationale-error" className="text-xs text-red-400" role="alert">
                        {overrideForm.formState.errors.rationale.message}
                      </p>
                    ) : null}
                  </div>
                  {overrideSubmitError ? (
                    <p className="text-sm text-red-400" role="alert">
                      {overrideSubmitError}
                    </p>
                  ) : null}
                  <Button type="submit" loading={overrideAction.pending} loadingLabel="Saving override…">Save override</Button>
                </form>
              </CardContent>
            </Card>
            <Card className="border-border bg-card/60">
              <CardHeader>
                <CardTitle className="text-lg">Workflow</CardTitle>
                <CardDescription>
                  {workflowTemplate
                    ? `Template: ${workflowTemplate}`
                    : "Track the proposed workflow steps for this work item."}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="workflow-template">Template</Label>
                    <Select
                      value={selectedWorkflowTemplate || noTemplateValue}
                      onValueChange={(value) =>
                        setSelectedWorkflowTemplate(
                          value === noTemplateValue ? "" : value
                        )
                      }
                      disabled={workflowTemplates.length === 0}
                    >
                      <SelectTrigger id="workflow-template">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {workflowTemplates.length === 0 ? (
                          <SelectItem value={noTemplateValue}>
                            No templates available
                          </SelectItem>
                        ) : null}
                        {workflowTemplates.map((template) => (
                          <SelectItem key={template.name} value={template.name}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedWorkflowSummary ? (
                      <p className="text-xs text-muted-foreground">
                        Steps: {selectedWorkflowSummary.steps.join(" / ")}
                      </p>
                    ) : null}
                    <Button
                      onClick={() => void handleTemplateApply()}
                      loading={templateAction.pending}
                      loadingLabel="Applying template…"
                      disabled={
                        !selectedWorkflowTemplate ||
                        selectedWorkflowTemplate === workflowTemplate
                      }
                    >
                      Apply template
                    </Button>
                  </div>
                  {workflowError ? (
                    <p className="text-sm text-red-400" role="alert">
                      {workflowError}
                    </p>
                  ) : workflowSteps.length === 0 ? (
                    <Empty className="border-border/60 bg-muted/40">
                      <EmptyHeader>
                        <EmptyTitle>No workflow steps yet</EmptyTitle>
                        <EmptyDescription className="text-muted-foreground">
                          Apply a template or add steps to get started.
                        </EmptyDescription>
                      </EmptyHeader>
                    </Empty>
                  ) : (
                    workflowSteps.map((step) => (
                      <div key={step.id} className="space-y-2">
                        <Input
                          type="text"
                          defaultValue={step.label}
                          onBlur={async (event) => {
                            try {
                              setWorkflowError(null);
                              const updated = await updateWorkflowStep(
                                entityDetail.entity.id,
                                step.id,
                                { label: event.target.value }
                              );
                              setWorkflowSteps((prev) =>
                                prev.map((item) =>
                                  item.id === step.id ? updated : item
                                )
                              );
                            } catch (err) {
                              const message =
                                err instanceof Error
                                  ? err.message
                                  : "Unable to update workflow.";
                              setWorkflowError(message);
                            }
                          }}
                        />
                        <Select
                          value={step.status}
                          onValueChange={async (value) => {
                            try {
                              setWorkflowError(null);
                              const updated = await updateWorkflowStep(
                                entityDetail.entity.id,
                                step.id,
                                {
                                  status: value as WorkflowStep["status"],
                                }
                              );
                              setWorkflowSteps((prev) =>
                                prev.map((item) =>
                                  item.id === step.id ? updated : item
                                )
                              );
                            } catch (err) {
                              const message =
                                err instanceof Error
                                  ? err.message
                                  : "Unable to update workflow.";
                              setWorkflowError(message);
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">pending</SelectItem>
                            <SelectItem value="in_progress">
                              in progress
                            </SelectItem>
                            <SelectItem value="done">done</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    ))
                  )}
              </CardContent>
            </Card>
            <Card className="border-border bg-card/60">
              <CardHeader>
                <CardTitle className="text-lg">Approval chain</CardTitle>
                <CardDescription>
                  Track sequential approvals required for this item.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-foreground/90">
                {entityDetail.entity.approvalChainProgress ? (
                  <>
                    <p className="text-xs text-muted-foreground">
                      Template: {entityDetail.entity.approvalChainProgress.templateName}
                    </p>
                    <div className="space-y-2">
                      {entityDetail.entity.approvalChainProgress.steps.map((step) => (
                        <div
                          key={step.id}
                          className="rounded-md border border-border/60 bg-muted/40 p-3"
                        >
                          <div className="flex items-center justify-between text-xs">
                            <span>
                              {step.position}. {step.label}
                            </span>
                            <span className="text-xs font-semibold text-foreground/80 capitalize">
                              {step.status}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Approver: {step.approverType} {step.approverId}
                          </p>
                          {step.approvedAt ? (
                            <p className="text-xs text-muted-foreground">
                              Approved {new Date(step.approvedAt).toLocaleString()} by{" "}
                              {step.actorType} {step.actorId}
                            </p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <Empty className="border-border/60 bg-muted/40">
                    <EmptyHeader>
                      <EmptyTitle>No approval chain configured</EmptyTitle>
                      <EmptyDescription className="text-muted-foreground">
                        High-priority work items surface multi-step approvals once configured.
                      </EmptyDescription>
                    </EmptyHeader>
                  </Empty>
                )}
              </CardContent>
            </Card>
            <Card className="border-border bg-card/60">
              <CardHeader>
                <CardTitle className="text-lg">Timeline</CardTitle>
                </CardHeader>
              <CardContent className="space-y-3 text-sm text-foreground/90">
                <form className="space-y-3 rounded-md border border-border/60 bg-muted/40 p-4" onSubmit={handleAuditExport} noValidate>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="space-y-1">
                      <Label htmlFor="audit-export-from">From</Label>
                      <Input id="audit-export-from" type="date" {...auditExportForm.register("from")} />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="audit-export-to">To</Label>
                      <Input
                        id="audit-export-to"
                        type="date"
                        {...auditExportForm.register("to")}
                        aria-invalid={Boolean(auditExportForm.formState.errors.to)}
                        aria-describedby={auditExportForm.formState.errors.to ? "audit-export-to-error" : undefined}
                      />
                      {auditExportForm.formState.errors.to ? (
                        <p id="audit-export-to-error" className="text-xs text-red-400" role="alert">
                          {auditExportForm.formState.errors.to.message}
                        </p>
                      ) : null}
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="audit-export-format">Format</Label>
                      <Controller
                        name="format"
                        control={auditExportForm.control}
                        render={({ field }) => (
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger id="audit-export-format">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="csv">CSV</SelectItem>
                              <SelectItem value="markdown">Markdown</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </div>
                    <div className="flex items-end">
                      <Button
                        type="submit"
                        className="w-full"
                        disabled={!entityDetail?.entity?.id}
                        loading={auditExportAction.pending}
                        loadingLabel="Preparing export…"
                      >
                        Export audit log
                      </Button>
                    </div>
                  </div>
                  {auditExportError ? (
                    <p className="text-xs text-red-400" role="alert">
                      {auditExportError}
                    </p>
                  ) : null}
                </form>
                {entityDetail.timeline.length === 0 ? (
                    <Empty className="border-border/60 bg-muted/40">
                      <EmptyHeader>
                        <EmptyTitle>No events yet</EmptyTitle>
                        <EmptyDescription className="text-muted-foreground">
                          Timeline updates will appear as work progresses.
                        </EmptyDescription>
                      </EmptyHeader>
                    </Empty>
                  ) : (
                    <>
                      {entityDetail.timeline.slice(0, visibleTimelineCount).map((event) => (
                      <div key={event.id} className="space-y-1">
                        <div>
                          <span className="text-muted-foreground/70">
                            {new Date(event.createdAt).toLocaleString()} -{" "}
                          </span>
                          {event.summary}
                        </div>
                        <p className="text-xs text-muted-foreground/70">
                          Actor: {event.actorType} {event.actorId}
                        </p>
                        {event.rationale ? (
                          <p className="text-xs text-muted-foreground">
                            Rationale: {event.rationale}
                          </p>
                        ) : null}
                        {event.contextExcerpt ? (
                          <pre className="mt-2 whitespace-pre-wrap rounded-md border border-border bg-muted/60 p-3 text-xs text-muted-foreground">
                            {event.contextExcerpt}
                          </pre>
                        ) : null}
                      </div>
                      ))}
                      {entityDetail.timeline.length > visibleTimelineCount ? (
                        <Button
                          variant="outline"
                          onClick={() =>
                            setVisibleTimelineCount((count) => count + 50)
                          }
                        >
                          Show earlier events (
                          {entityDetail.timeline.length - visibleTimelineCount})
                        </Button>
                      ) : null}
                    </>
                  )}
                </CardContent>
              </Card>
          </>
        ) : null}
      </section>
    </PageShell>
  );
}
